/**
 * NLP Command Service - parses natural language commands for the search palette
 */

// Navigation command patterns
const NAVIGATION_PATTERNS = [
  { regex: /^(?:go to|open|show|navigate to|view)\s+(.+)/i, type: 'navigate' },
  { regex: /^(?:find|search|look for|search for)\s+(.+)/i, type: 'search' },
  { regex: /^(?:create|new|add)\s+(.+)/i, type: 'create' },
];

// Page aliases for natural language matching
const PAGE_ALIASES = {
  'dashboard': ['home', 'main', 'overview', 'start', 'dash'],
  'members': ['patients', 'people', 'screenings', 'records', 'member'],
  'bookings': ['events', 'appointments', 'calendar', 'schedule', 'booking'],
  'intake': ['queue', 'patient queue', 'waiting', 'check-in', 'checkin'],
  'users': ['staff', 'team', 'employees', 'accounts', 'user'],
  'cases': ['case management', 'follow-up', 'care', 'case'],
  'call-logs': ['calls', 'phone', 'call history', 'phone logs', 'call log'],
  'volunteers': ['volunteer', 'helpers', 'registration', 'volunter'],
  'analytics': ['stats', 'statistics', 'reports', 'metrics', 'data', 'analytic'],
  'settings': ['preferences', 'config', 'configuration', 'options', 'setting'],
  'audit-logs': ['logs', 'audit', 'activity', 'history', 'log'],
};

// Command templates for autocomplete
const COMMAND_TEMPLATES = {
  'go to': ['go to dashboard', 'go to members', 'go to bookings', 'go to analytics', 'go to settings'],
  'open': ['open dashboard', 'open members', 'open call logs', 'open volunteers'],
  'show': ['show members', 'show analytics', 'show call logs', 'show volunteers'],
  'find': ['find members named', 'find volunteers from', 'find events in'],
  'search': ['search members', 'search events', 'search volunteers', 'search calls'],
  'view': ['view analytics', 'view members', 'view call logs', 'view volunteers'],
};

/**
 * Parse a natural language command from the query
 * @param {string} query - The search query
 * @returns {object|null} - Parsed command object or null
 */
export const parseNLPCommand = (query) => {
  if (!query || query.length < 3) return null;
  
  const lowerQuery = query.toLowerCase().trim();
  
  // Check each pattern
  for (const pattern of NAVIGATION_PATTERNS) {
    const match = lowerQuery.match(pattern.regex);
    if (match) {
      const target = match[1].trim();
      
      // Try to resolve the target to a page
      const resolvedPage = resolvePageFromAlias(target);
      
      return {
        type: pattern.type,
        target,
        resolvedPage,
        originalQuery: query
      };
    }
  }
  
  return null;
};

/**
 * Resolve a page from an alias or name
 * @param {string} input - The input string to resolve
 * @returns {string|null} - The page key or null
 */
const resolvePageFromAlias = (input) => {
  const lowerInput = input.toLowerCase();
  
  // Direct match
  if (PAGE_ALIASES[lowerInput]) {
    return lowerInput;
  }
  
  // Check aliases
  for (const [pageKey, aliases] of Object.entries(PAGE_ALIASES)) {
    if (aliases.some(alias => lowerInput.includes(alias) || alias.includes(lowerInput))) {
      return pageKey;
    }
  }
  
  return null;
};

/**
 * Get command suggestions based on partial input
 * @param {string} query - The partial query
 * @returns {string[]} - Array of suggested commands
 */
export const getCommandSuggestions = (query) => {
  if (!query || query.length < 2) return [];
  
  const lowerQuery = query.toLowerCase().trim();
  const suggestions = [];
  
  // Find matching command templates
  for (const [prefix, templates] of Object.entries(COMMAND_TEMPLATES)) {
    if (prefix.startsWith(lowerQuery) || lowerQuery.startsWith(prefix)) {
      templates.forEach(template => {
        if (template.toLowerCase().startsWith(lowerQuery) && template.toLowerCase() !== lowerQuery) {
          suggestions.push(template);
        }
      });
    }
  }
  
  return [...new Set(suggestions)].slice(0, 5);
};

/**
 * Get contextual suggestions based on current page
 * @param {string} currentPath - The current page path
 * @returns {string[]} - Array of contextual suggestions
 */
export const getContextualSuggestions = (currentPath) => {
  const suggestions = [];
  
  if (currentPath.includes('/members') || currentPath.includes('/screenings')) {
    suggestions.push('search members by name', 'view member details', 'export member data');
  } else if (currentPath.includes('/events')) {
    suggestions.push('create new event', 'view event bookings', 'search events');
  } else if (currentPath.includes('/call-logs')) {
    suggestions.push('view calls from today', 'search calls by number', 'export call logs');
  } else if (currentPath.includes('/volunteers')) {
    suggestions.push('search volunteers', 'view volunteer analytics', 'export volunteer data');
  } else if (currentPath.includes('/analytics')) {
    suggestions.push('view health outcomes', 'view operations metrics', 'export analytics');
  } else if (currentPath.includes('/dashboard')) {
    suggestions.push('go to members', 'view analytics', 'open call logs');
  }
  
  return suggestions;
};

/**
 * Highlight matching text in a string
 * @param {string} text - The text to highlight
 * @param {string} query - The query to highlight
 * @returns {object} - Object with parts for rendering
 */
export const highlightMatch = (text, query) => {
  if (!query || !text) return { text, parts: [{ text, highlight: false }] };
  
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);
  
  if (index === -1) {
    return { text, parts: [{ text, highlight: false }] };
  }
  
  return {
    text,
    parts: [
      { text: text.slice(0, index), highlight: false },
      { text: text.slice(index, index + query.length), highlight: true },
      { text: text.slice(index + query.length), highlight: false }
    ].filter(part => part.text)
  };
};

export default {
  parseNLPCommand,
  getCommandSuggestions,
  getContextualSuggestions,
  highlightMatch
};
